import { onBeforeUnmount, ref, shallowRef } from 'vue'
import { onSessionCleared } from "@/stores/auth"

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closing' | 'closed' | 'error'

export interface WsMessage<T = unknown> {
  type: string
  data?: T
  ts?: number
}

export interface UseWebSocketOptions {
  url: string | (() => string)
  protocols?: string | string[]
  autoReconnect?: boolean
  reconnectDelay?: number
  maxReconnectDelay?: number
  heartbeatInterval?: number
  heartbeatMessage?: string
  onMessage?: (msg: WsMessage) => void
  onOpen?: () => void
  onClose?: () => void
  onError?: (ev: Event) => void
  /** v3.14.0: 会话清除 (登出/401) 时断开并停止重连 — 防旧会话无限重连循环。 */
  stopOnSessionClear?: boolean
}

export function useWebSocket(options: UseWebSocketOptions) {
  const {
    url,
    protocols,
    autoReconnect = true,
    reconnectDelay = 1000,
    maxReconnectDelay = 15_000,
    heartbeatInterval = 25_000,
    heartbeatMessage = JSON.stringify({ type: 'ping' }),
    onMessage,
    onOpen,
    onClose,
    onError,
    stopOnSessionClear = false,
  } = options

  const status = ref<WsStatus>('idle')
  const lastMessage = shallowRef<WsMessage | null>(null)
  const reconnectAttempts = ref(0)
  const subscribers = new Set<(msg: WsMessage) => void>()

  let socket: WebSocket | null = null
  let reconnectTimer: number | null = null
  let heartbeatTimer: number | null = null
  let closedByUser = false

  // v3.14.0 (UI 打磨): 会话清除 (登出/401) → 断开且不再重连。此前登出
  // 只清凭证, WS 持旧会话打 401 → autoReconnect 1.5s 重试 → 死循环,
  // 直到页面刷新; 服务端 WS 升级也会持续消耗。
  if (stopOnSessionClear) {
    onSessionCleared(() => {
      closedByUser = true
      disconnect()
    })
  }

  function resolveUrl(): string {
    return typeof url === 'function' ? url() : url
  }

  function clearTimers(): void {
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    if (heartbeatTimer !== null) {
      window.clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
  }

  function startHeartbeat(): void {
    if (!heartbeatInterval || heartbeatInterval <= 0) return
    if (heartbeatTimer !== null) window.clearInterval(heartbeatTimer)
    heartbeatTimer = window.setInterval(() => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(heartbeatMessage)
        } catch {
          /* ignore */
        }
      }
    }, heartbeatInterval)
  }

  function scheduleReconnect(): void {
    if (!autoReconnect || closedByUser) return
    const attempt = reconnectAttempts.value + 1
    const delay = Math.min(reconnectDelay * 2 ** (attempt - 1), maxReconnectDelay)
    reconnectAttempts.value = attempt
    reconnectTimer = window.setTimeout(connect, delay)
  }

  function connect(): void {
    if (typeof WebSocket === 'undefined') {
      status.value = 'error'
      return
    }
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      return
    }
    closedByUser = false
    status.value = 'connecting'
    try {
      socket = protocols ? new WebSocket(resolveUrl(), protocols) : new WebSocket(resolveUrl())
    } catch {
      status.value = 'error'
      scheduleReconnect()
      return
    }

    socket.addEventListener('open', () => {
      status.value = 'open'
      reconnectAttempts.value = 0
      startHeartbeat()
      onOpen?.()
    })

    socket.addEventListener('message', (event: MessageEvent<string>) => {
      let parsed: WsMessage | null = null
      try {
        const raw = event.data
        const obj = typeof raw === 'string' ? JSON.parse(raw) : raw
        parsed = obj as WsMessage
      } catch {
        parsed = { type: 'raw', data: event.data }
      }
      if (parsed) {
        lastMessage.value = parsed
        subscribers.forEach((cb) => {
          try {
            cb(parsed as WsMessage)
          } catch {
            /* ignore */
          }
        })
        onMessage?.(parsed)
      }
    })

    socket.addEventListener('error', (ev) => {
      status.value = 'error'
      onError?.(ev)
    })

    socket.addEventListener('close', () => {
      status.value = 'closed'
      clearTimers()
      onClose?.()
      if (!closedByUser) scheduleReconnect()
    })
  }

  function disconnect(): void {
    closedByUser = true
    clearTimers()
    if (socket) {
      status.value = 'closing'
      try {
        socket.close()
      } catch {
        /* ignore */
      }
      socket = null
    }
  }

  function send(payload: unknown): boolean {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false
    const data = typeof payload === 'string' ? payload : JSON.stringify(payload)
    try {
      socket.send(data)
      return true
    } catch {
      return false
    }
  }

  function subscribe(cb: (msg: WsMessage) => void): () => void {
    subscribers.add(cb)
    return () => {
      subscribers.delete(cb)
    }
  }

  onBeforeUnmount(disconnect)

  return {
    status,
    lastMessage,
    reconnectAttempts,
    connect,
    disconnect,
    send,
    subscribe,
  }
}
